/**
 * Mock Approval Processes Data
 *
 * Provides realistic sample data for approval processes and requests.
 * Used for development and demonstration purposes.
 *
 * @module data/mockApprovalProcesses
 */

import {
  ApprovalProcess,
  ApprovalRequest,
  ApprovalStepHistory
} from '../types/approvalProcesses';

// ============================================
// DATE HELPERS
// ============================================
const today = new Date();
const daysAgo = (days: number) => {
  const date = new Date(today);
  date.setDate(date.getDate() - days);
  return date.toISOString();
};
const monthsAgo = (months: number) => {
  const date = new Date(today);
  date.setMonth(date.getMonth() - months);
  return date.toISOString();
};

// ============================================
// MOCK APPROVAL PROCESSES
// ============================================

export const mockApprovalProcesses: ApprovalProcess[] = [
  {
    id: 'ap_001',
    name: 'Opportunity Discount Approval',
    description: 'Required approval for opportunity discounts exceeding 15%. Routes through sales manager and director for large discounts.',
    objectType: 'Opportunity',
    isActive: true,
    order: 1,
    entryCriteria: [
      {
        id: 'ec_001_1',
        field: 'discountPercent',
        operator: 'greater_than',
        value: 15,
        logic: 'AND'
      },
      {
        id: 'ec_001_2',
        field: 'stage',
        operator: 'equals',
        value: 'Evaluation'
      }
    ],
    entryCriteriaLogic: 'AND',
    steps: [
      {
        id: 'step_001_1',
        stepNumber: 1,
        name: 'Sales Manager Approval',
        description: 'Initial review by the direct sales manager',
        approverType: 'manager',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 3,
        escalateTo: 'role_sales_director',
        autoApproveCondition: 'discountPercent <= 20'
      },
      {
        id: 'step_001_2',
        stepNumber: 2,
        name: 'Sales Director Approval',
        description: 'Final approval for discounts over 20%',
        approverType: 'role',
        approverId: 'role_sales_director',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 5,
        escalateTo: 'role_vp_sales'
      }
    ],
    initialSubmissionActions: [
      {
        id: 'isa_001_1',
        type: 'field_update',
        name: 'Set Approval Status',
        config: { field: 'approvalStatus', value: 'Pending Approval' }
      },
      {
        id: 'isa_001_2',
        type: 'email_alert',
        name: 'Notify Approver',
        config: { templateId: 'tmpl_approval_request' }
      }
    ],
    finalApprovalActions: [
      {
        id: 'faa_001_1',
        type: 'field_update',
        name: 'Mark as Approved',
        config: { field: 'approvalStatus', value: 'Approved' }
      },
      {
        id: 'faa_001_2',
        type: 'email_alert',
        name: 'Notify Submitter of Approval',
        config: { templateId: 'tmpl_approval_approved' }
      }
    ],
    finalRejectionActions: [
      {
        id: 'fra_001_1',
        type: 'field_update',
        name: 'Mark as Rejected',
        config: { field: 'approvalStatus', value: 'Rejected' }
      },
      {
        id: 'fra_001_2',
        type: 'email_alert',
        name: 'Notify Submitter of Rejection',
        config: { templateId: 'tmpl_approval_rejected' }
      }
    ],
    recallActions: [
      {
        id: 'ra_001_1',
        type: 'field_update',
        name: 'Clear Approval Status',
        config: { field: 'approvalStatus', value: 'Draft' }
      }
    ],
    allowRecall: true,
    recordEditability: 'locked',
    approverCanEdit: false,
    approvalEmailTemplateId: 'tmpl_approval_request',
    rejectionEmailTemplateId: 'tmpl_approval_rejected',
    createdDate: daysAgo(30).split('T')[0] + 'T10:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: daysAgo(2).split('T')[0] + 'T14:30:00Z',
    lastModifiedBy: 'admin'
  },
  {
    id: 'ap_002',
    name: 'Lead Conversion Approval',
    description: 'Approval required before converting high-value leads to opportunities. Ensures proper qualification.',
    objectType: 'Lead',
    isActive: true,
    order: 1,
    entryCriteria: [
      {
        id: 'ec_002_1',
        field: 'estimatedValue',
        operator: 'greater_than',
        value: 50000,
        logic: 'AND'
      },
      {
        id: 'ec_002_2',
        field: 'status',
        operator: 'equals',
        value: 'Qualified'
      }
    ],
    entryCriteriaLogic: 'AND',
    steps: [
      {
        id: 'step_002_1',
        stepNumber: 1,
        name: 'Marketing Qualification Review',
        description: 'Marketing team reviews lead qualification',
        approverType: 'role',
        approverId: 'role_marketing_manager',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 2
      },
      {
        id: 'step_002_2',
        stepNumber: 2,
        name: 'Sales Manager Approval',
        description: 'Sales manager confirms lead assignment',
        approverType: 'manager',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 2
      }
    ],
    initialSubmissionActions: [
      {
        id: 'isa_002_1',
        type: 'field_update',
        name: 'Set Status to Pending',
        config: { field: 'conversionStatus', value: 'Pending Approval' }
      }
    ],
    finalApprovalActions: [
      {
        id: 'faa_002_1',
        type: 'field_update',
        name: 'Set Status to Approved',
        config: { field: 'conversionStatus', value: 'Approved for Conversion' }
      },
      {
        id: 'faa_002_2',
        type: 'task_creation',
        name: 'Create Conversion Task',
        config: {
          subject: 'Convert Approved Lead',
          priority: 'High',
          dueInDays: 1
        }
      }
    ],
    finalRejectionActions: [
      {
        id: 'fra_002_1',
        type: 'field_update',
        name: 'Set Status to Rejected',
        config: { field: 'conversionStatus', value: 'Not Qualified' }
      }
    ],
    recallActions: [],
    allowRecall: true,
    recordEditability: 'admin_only',
    approverCanEdit: true,
    createdDate: daysAgo(25).split('T')[0] + 'T09:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: daysAgo(3).split('T')[0] + 'T11:00:00Z',
    lastModifiedBy: 'admin'
  },
  {
    id: 'ap_003',
    name: 'Case Escalation Approval',
    description: 'Approval process for escalating support cases to senior support or engineering.',
    objectType: 'Case',
    isActive: true,
    order: 1,
    entryCriteria: [
      {
        id: 'ec_003_1',
        field: 'priority',
        operator: 'equals',
        value: 'Critical',
        logic: 'OR'
      },
      {
        id: 'ec_003_2',
        field: 'daysOpen',
        operator: 'greater_than',
        value: 5
      }
    ],
    entryCriteriaLogic: 'OR',
    steps: [
      {
        id: 'step_003_1',
        stepNumber: 1,
        name: 'Support Manager Review',
        description: 'Support manager reviews escalation request',
        approverType: 'role',
        approverId: 'role_support_manager',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 1,
        rejectionReasons: [
          'Insufficient information provided',
          'Issue can be resolved at current level',
          'Customer not responsive'
        ]
      },
      {
        id: 'step_003_2',
        stepNumber: 2,
        name: 'Engineering Assignment',
        description: 'Engineering lead assigns resources',
        approverType: 'queue',
        approverId: 'queue_engineering',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 1
      }
    ],
    initialSubmissionActions: [
      {
        id: 'isa_003_1',
        type: 'field_update',
        name: 'Set Escalation Status',
        config: { field: 'escalationStatus', value: 'Pending Review' }
      },
      {
        id: 'isa_003_2',
        type: 'email_alert',
        name: 'Alert Support Team',
        config: { templateId: 'tmpl_case_escalation' }
      }
    ],
    finalApprovalActions: [
      {
        id: 'faa_003_1',
        type: 'field_update',
        name: 'Mark as Escalated',
        config: { field: 'escalationStatus', value: 'Escalated' }
      },
      {
        id: 'faa_003_2',
        type: 'field_update',
        name: 'Update Priority',
        config: { field: 'priority', value: 'Critical' }
      }
    ],
    finalRejectionActions: [
      {
        id: 'fra_003_1',
        type: 'field_update',
        name: 'Clear Escalation',
        config: { field: 'escalationStatus', value: 'Not Escalated' }
      }
    ],
    recallActions: [],
    allowRecall: false,
    recordEditability: 'unlocked',
    approverCanEdit: true,
    createdDate: daysAgo(20).split('T')[0] + 'T14:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: daysAgo(5).split('T')[0] + 'T16:00:00Z',
    lastModifiedBy: 'admin'
  },
  {
    id: 'ap_004',
    name: 'Contract Approval',
    description: 'Multi-level approval for new contracts and renewals. Routes through legal and finance.',
    objectType: 'Contract',
    isActive: true,
    order: 1,
    entryCriteria: [
      {
        id: 'ec_004_1',
        field: 'totalValue',
        operator: 'greater_than',
        value: 10000
      }
    ],
    entryCriteriaLogic: 'AND',
    steps: [
      {
        id: 'step_004_1',
        stepNumber: 1,
        name: 'Legal Review',
        description: 'Legal team reviews contract terms',
        approverType: 'role',
        approverId: 'role_legal',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 5
      },
      {
        id: 'step_004_2',
        stepNumber: 2,
        name: 'Finance Approval',
        description: 'Finance reviews payment terms and budget',
        approverType: 'role',
        approverId: 'role_finance',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 3,
        autoApproveCondition: 'totalValue <= 25000'
      },
      {
        id: 'step_004_3',
        stepNumber: 3,
        name: 'Executive Sign-off',
        description: 'Final executive approval for high-value contracts',
        approverType: 'role',
        approverId: 'role_executive',
        unanimousApproval: false,
        allowDelegation: false,
        autoApproveCondition: 'totalValue <= 50000'
      }
    ],
    initialSubmissionActions: [
      {
        id: 'isa_004_1',
        type: 'field_update',
        name: 'Set Contract Status',
        config: { field: 'status', value: 'Pending Approval' }
      }
    ],
    finalApprovalActions: [
      {
        id: 'faa_004_1',
        type: 'field_update',
        name: 'Activate Contract',
        config: { field: 'status', value: 'Active' }
      },
      {
        id: 'faa_004_2',
        type: 'email_alert',
        name: 'Send Welcome Email',
        config: { templateId: 'tmpl_contract_welcome' }
      }
    ],
    finalRejectionActions: [
      {
        id: 'fra_004_1',
        type: 'field_update',
        name: 'Mark as Rejected',
        config: { field: 'status', value: 'Rejected' }
      }
    ],
    recallActions: [
      {
        id: 'ra_004_1',
        type: 'field_update',
        name: 'Reset to Draft',
        config: { field: 'status', value: 'Draft' }
      }
    ],
    allowRecall: true,
    recordEditability: 'locked',
    approverCanEdit: false,
    createdDate: daysAgo(22).split('T')[0] + 'T11:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: daysAgo(1).split('T')[0] + 'T09:30:00Z',
    lastModifiedBy: 'admin'
  },
  {
    id: 'ap_005',
    name: 'Credit Application Review',
    description: 'Credit approval workflow for client credit applications in credit management system.',
    objectType: 'CreditApplication',
    isActive: false,
    order: 1,
    entryCriteria: [
      {
        id: 'ec_005_1',
        field: 'applicationType',
        operator: 'in_list',
        value: ['Credit Repair', 'Credit Development', 'ID Theft Recovery']
      }
    ],
    entryCriteriaLogic: 'AND',
    steps: [
      {
        id: 'step_005_1',
        stepNumber: 1,
        name: 'Initial Document Review',
        description: 'Verify all required documents are submitted',
        approverType: 'role',
        approverId: 'role_credit_analyst',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 2
      },
      {
        id: 'step_005_2',
        stepNumber: 2,
        name: 'Credit Manager Approval',
        description: 'Credit manager reviews application',
        approverType: 'role',
        approverId: 'role_credit_manager',
        unanimousApproval: false,
        allowDelegation: true,
        autoEscalateAfterDays: 3
      }
    ],
    initialSubmissionActions: [
      {
        id: 'isa_005_1',
        type: 'field_update',
        name: 'Set Application Status',
        config: { field: 'status', value: 'Under Review' }
      }
    ],
    finalApprovalActions: [
      {
        id: 'faa_005_1',
        type: 'field_update',
        name: 'Approve Application',
        config: { field: 'status', value: 'Approved' }
      },
      {
        id: 'faa_005_2',
        type: 'task_creation',
        name: 'Create Onboarding Task',
        config: {
          subject: 'Begin Client Onboarding',
          priority: 'High',
          dueInDays: 1
        }
      }
    ],
    finalRejectionActions: [
      {
        id: 'fra_005_1',
        type: 'field_update',
        name: 'Reject Application',
        config: { field: 'status', value: 'Rejected' }
      }
    ],
    recallActions: [],
    allowRecall: true,
    recordEditability: 'admin_only',
    approverCanEdit: true,
    createdDate: daysAgo(18).split('T')[0] + 'T08:00:00Z',
    createdBy: 'admin',
    lastModifiedDate: daysAgo(18).split('T')[0] + 'T08:00:00Z',
    lastModifiedBy: 'admin'
  }
];

// ============================================
// MOCK APPROVAL REQUESTS
// ============================================

const hoursAgo = (hours: number) => new Date(today.getTime() - hours * 60 * 60 * 1000).toISOString();

export const mockApprovalRequests: ApprovalRequest[] = [
  {
    id: 'ar_001',
    processId: 'ap_001',
    processName: 'Opportunity Discount Approval',
    recordId: 'opp_12345',
    recordName: 'Acme Corp - Enterprise License',
    objectType: 'Opportunity',
    status: 'pending',
    currentStepNumber: 1,
    totalSteps: 2,
    submittedBy: 'user_john',
    submittedByName: 'John Smith',
    submittedDate: hoursAgo(4),
    comments: 'Requesting 25% discount to close this quarter. Customer is strategic.',
    stepHistory: [
      {
        id: 'sh_001_1',
        stepNumber: 1,
        stepName: 'Sales Manager Approval',
        assignedTo: 'user_sarah',
        assignedToName: 'Sarah Johnson',
        assignedDate: hoursAgo(4),
        status: 'pending',
        dueDate: new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString()
      }
    ]
  },
  {
    id: 'ar_002',
    processId: 'ap_001',
    processName: 'Opportunity Discount Approval',
    recordId: 'opp_12346',
    recordName: 'TechStart Inc - Annual Subscription',
    objectType: 'Opportunity',
    status: 'pending',
    currentStepNumber: 2,
    totalSteps: 2,
    submittedBy: 'user_mike',
    submittedByName: 'Mike Davis',
    submittedDate: daysAgo(2),
    comments: '30% discount for first-year customer. High growth potential.',
    stepHistory: [
      {
        id: 'sh_002_1',
        stepNumber: 1,
        stepName: 'Sales Manager Approval',
        assignedTo: 'user_sarah',
        assignedToName: 'Sarah Johnson',
        assignedDate: daysAgo(2),
        action: 'approve',
        actionDate: daysAgo(1),
        actionBy: 'user_sarah',
        actionByName: 'Sarah Johnson',
        comments: 'Approved. Strategic account.',
        status: 'completed'
      },
      {
        id: 'sh_002_2',
        stepNumber: 2,
        stepName: 'Sales Director Approval',
        assignedTo: 'user_director',
        assignedToName: 'Sales Director',
        assignedDate: daysAgo(1),
        status: 'pending',
        dueDate: new Date(today.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString()
      }
    ]
  },
  {
    id: 'ar_003',
    processId: 'ap_002',
    processName: 'Lead Conversion Approval',
    recordId: 'lead_789',
    recordName: 'Global Industries - Enterprise Lead',
    objectType: 'Lead',
    status: 'approved',
    currentStepNumber: 2,
    totalSteps: 2,
    submittedBy: 'user_emily',
    submittedByName: 'Emily Chen',
    submittedDate: daysAgo(5),
    comments: 'High-value lead ready for conversion. Budget confirmed.',
    stepHistory: [
      {
        id: 'sh_003_1',
        stepNumber: 1,
        stepName: 'Marketing Qualification Review',
        assignedTo: 'user_marketing',
        assignedToName: 'Marketing Manager',
        assignedDate: daysAgo(5),
        action: 'approve',
        actionDate: daysAgo(4),
        actionBy: 'user_marketing',
        actionByName: 'Marketing Manager',
        comments: 'Lead meets all qualification criteria.',
        status: 'completed'
      },
      {
        id: 'sh_003_2',
        stepNumber: 2,
        stepName: 'Sales Manager Approval',
        assignedTo: 'user_sarah',
        assignedToName: 'Sarah Johnson',
        assignedDate: daysAgo(4),
        action: 'approve',
        actionDate: daysAgo(3),
        actionBy: 'user_sarah',
        actionByName: 'Sarah Johnson',
        comments: 'Approved for conversion. Assigned to John.',
        status: 'completed'
      }
    ],
    completedDate: daysAgo(3),
    finalComments: 'Approved for conversion. Assigned to John.'
  },
  {
    id: 'ar_004',
    processId: 'ap_003',
    processName: 'Case Escalation Approval',
    recordId: 'case_456',
    recordName: 'Critical Bug - Data Export Failure',
    objectType: 'Case',
    status: 'rejected',
    currentStepNumber: 1,
    totalSteps: 2,
    submittedBy: 'user_support',
    submittedByName: 'Support Agent',
    submittedDate: daysAgo(3),
    comments: 'Customer experiencing critical data export failures.',
    stepHistory: [
      {
        id: 'sh_004_1',
        stepNumber: 1,
        stepName: 'Support Manager Review',
        assignedTo: 'user_support_mgr',
        assignedToName: 'Support Manager',
        assignedDate: daysAgo(3),
        action: 'reject',
        actionDate: daysAgo(2),
        actionBy: 'user_support_mgr',
        actionByName: 'Support Manager',
        comments: 'Issue can be resolved at current level. Workaround available.',
        status: 'completed'
      }
    ],
    completedDate: daysAgo(2),
    finalComments: 'Issue can be resolved at current level. Workaround available.'
  },
  {
    id: 'ar_005',
    processId: 'ap_004',
    processName: 'Contract Approval',
    recordId: 'contract_001',
    recordName: 'Mega Corp - 3 Year Enterprise Agreement',
    objectType: 'Contract',
    status: 'pending',
    currentStepNumber: 2,
    totalSteps: 3,
    submittedBy: 'user_sales',
    submittedByName: 'Sales Rep',
    submittedDate: daysAgo(4),
    comments: '$150,000 3-year contract. Special payment terms requested.',
    stepHistory: [
      {
        id: 'sh_005_1',
        stepNumber: 1,
        stepName: 'Legal Review',
        assignedTo: 'user_legal',
        assignedToName: 'Legal Team',
        assignedDate: daysAgo(4),
        action: 'approve',
        actionDate: daysAgo(2),
        actionBy: 'user_legal',
        actionByName: 'Legal Counsel',
        comments: 'Terms reviewed and approved with minor modifications.',
        status: 'completed'
      },
      {
        id: 'sh_005_2',
        stepNumber: 2,
        stepName: 'Finance Approval',
        assignedTo: 'user_finance',
        assignedToName: 'Finance Team',
        assignedDate: daysAgo(2),
        status: 'pending',
        dueDate: new Date(today.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString()
      }
    ]
  },
  {
    id: 'ar_006',
    processId: 'ap_001',
    processName: 'Opportunity Discount Approval',
    recordId: 'opp_12347',
    recordName: 'StartupX - First Year Deal',
    objectType: 'Opportunity',
    status: 'recalled',
    currentStepNumber: 1,
    totalSteps: 2,
    submittedBy: 'user_john',
    submittedByName: 'John Smith',
    submittedDate: daysAgo(6),
    comments: 'Requesting 20% startup discount.',
    stepHistory: [
      {
        id: 'sh_006_1',
        stepNumber: 1,
        stepName: 'Sales Manager Approval',
        assignedTo: 'user_sarah',
        assignedToName: 'Sarah Johnson',
        assignedDate: daysAgo(6),
        action: 'recall',
        actionDate: daysAgo(5),
        actionBy: 'user_john',
        actionByName: 'John Smith',
        comments: 'Deal terms changed. Will resubmit.',
        status: 'completed'
      }
    ],
    completedDate: daysAgo(5),
    finalComments: 'Deal terms changed. Will resubmit.'
  }
];

// ============================================
// INITIALIZATION HELPER
// ============================================

/**
 * Initialize approval process mock data in localStorage
 */
export const initializeApprovalProcessData = (): void => {
  const processesKey = 'crm_approval_processes';
  const requestsKey = 'crm_approval_requests';

  // Only initialize if empty
  const existingProcesses = localStorage.getItem(processesKey);
  const existingRequests = localStorage.getItem(requestsKey);

  if (!existingProcesses) {
    localStorage.setItem(processesKey, JSON.stringify(mockApprovalProcesses));
    console.log('Initialized mock approval processes');
  }

  if (!existingRequests) {
    localStorage.setItem(requestsKey, JSON.stringify(mockApprovalRequests));
    console.log('Initialized mock approval requests');
  }
};
